import type { ReactNode } from 'react'
import { CloseButton, TextInput, type TextInputProps } from '@mantine/core'
import { IconSearch } from '@tabler/icons-react'
import type { CrmLeadStatus } from '@behuman/core-contract'
import type { Lead } from './hooks'
import { CHIP_TONES, LEAD_STATUS_COLORS, LEAD_STATUS_LABELS, type ChipColor } from './labels'

/**
 * The CRM's building blocks (PD-1993), drawn from the Patient list so the two read as one
 * product: the rounded card, the soft status chips, tinted initials and the coral main action.
 */

/** The Patient list's "Add patient" coral, kept for each view's one main action. */
export const CORAL = '#E5533D'

export const tableHeaderClass = 'text-[11px] font-semibold tracking-wider text-[#6B7587] uppercase'
export const tableRowClass =
  'h-[56px] cursor-pointer border-b border-[#F0F1F3] text-sm text-[#4A5160] transition-colors hover:bg-[#FAFBFC]'

export function CrmChip({ color, children }: { color: ChipColor; children: ReactNode }) {
  const tone = CHIP_TONES[color]
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[13px] font-semibold whitespace-nowrap"
      style={{ backgroundColor: tone.bg, color: tone.fg }}
    >
      {children}
    </span>
  )
}

export function LeadStatusBadge({ status }: { status: CrmLeadStatus }) {
  return <CrmChip color={LEAD_STATUS_COLORS[status]}>{LEAD_STATUS_LABELS[status]}</CrmChip>
}

/** A count beside a title, as the Patient list shows its total. */
export function CountChip({ children }: { children: ReactNode }) {
  return (
    <span className="rounded-full bg-[#F1F2F4] px-2.5 py-0.5 text-sm font-semibold text-[#4A5160] tabular-nums">
      {children}
    </span>
  )
}

const AVATAR_TONES = [
  { bg: '#FDE8EE', fg: '#B8325A' },
  { bg: '#E8EEF7', fg: '#3D5A80' },
  { bg: '#FDEBE4', fg: '#C2410C' },
  { bg: '#E6F4EA', fg: '#1E6B3A' },
  { bg: '#F1E9FB', fg: '#7C3AAD' },
]

/** The lead's initials on a tint picked by id, so a lead keeps its colour across lists. */
export function LeadAvatar({ lead }: { lead: Lead }) {
  const initials = `${lead.firstName?.[0] ?? ''}${lead.lastName?.[0] ?? ''}`.toUpperCase() || '#'
  const tone = AVATAR_TONES[lead.id % AVATAR_TONES.length]
  return (
    <span
      aria-hidden
      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold"
      style={{ backgroundColor: tone.bg, color: tone.fg }}
    >
      {initials}
    </span>
  )
}

/** The coral icon tile that opens a CRM dialog's header. */
export function CrmIconTile({ children }: { children: ReactNode }) {
  return (
    <span
      aria-hidden
      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#FDEBE4]"
      style={{ color: CORAL }}
    >
      {children}
    </span>
  )
}

/**
 * A CRM view: the Patient list's card, its header holding the title and count, the view
 * switcher, the view's own tools, and its main action on the right.
 */
export function CrmCard({
  icon,
  count,
  switcher,
  tools,
  action,
  className = '',
  children,
}: {
  icon: ReactNode
  count?: number
  switcher?: ReactNode
  tools?: ReactNode
  action?: ReactNode
  /** Added to the card's own classes, e.g. a height. */
  className?: string
  children: ReactNode
}) {
  return (
    <div
      className={`flex flex-col gap-4 rounded-[18px] border border-[#E7E9EC] bg-white px-6 pt-6 pb-4 shadow-[0_1px_3px_rgba(16,24,40,.05),0_12px_30px_rgba(16,24,40,.05)] ${className}`}
    >
      <div className="flex flex-wrap items-center gap-5">
        <div className="flex items-center gap-2.5">
          {icon}
          <h1 className="text-xl font-bold tracking-tight text-[#1F2733]">CRM</h1>
          {count !== undefined && <CountChip>{count}</CountChip>}
        </div>
        {switcher}
        {tools && <div className="flex flex-wrap items-center gap-3">{tools}</div>}
        {action && <div className="ml-auto flex shrink-0 items-center">{action}</div>}
      </div>
      {children}
    </div>
  )
}

/**
 * A CRM search box: the magnifier on the left, and an × on the right once something is typed
 * that empties it. A `rightSection` given (e.g. a loader) takes the ×'s place.
 */
export function CrmSearchInput({
  label,
  onChange,
  ...props
}: Omit<TextInputProps, 'onChange' | 'value'> & {
  label: string
  value: string
  onChange: (value: string) => void
}) {
  return (
    <TextInput
      aria-label={label}
      radius="md"
      leftSection={<IconSearch size={16} className="text-[#9AA0A8]" />}
      rightSection={
        props.value && (
          <CloseButton
            size="sm"
            aria-label={`Clear ${label.toLowerCase()}`}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => onChange('')}
          />
        )
      }
      rightSectionPointerEvents="all"
      {...props}
      onChange={(event) => onChange(event.currentTarget.value)}
    />
  )
}

/**
 * A row's name as a real button, so the row it opens can be reached from the keyboard. The click
 * stops here: a row that opens on click too would otherwise open twice.
 */
export function NameButton({ name, onClick }: { name: string; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-label={`Open ${name}`}
      onClick={(event) => {
        event.stopPropagation()
        onClick()
      }}
      className="cursor-pointer rounded border-0 bg-transparent p-0 text-left text-sm font-bold text-[#1F2733] hover:text-[#24548A] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#316DAB]"
    >
      {name}
    </button>
  )
}
